import React from 'react';
import { Cpu, Radio, Layers, Wrench, ArrowRight } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { IMAGES } from '../mock/mock';

const tagIcons = [Cpu, Radio, Layers, Wrench];

const Technology = () => {
  const { t, lang } = useLang();
  const rtl = lang === 'ar';
  return (
    <section id="technology" className="relative py-32 md:py-40 border-t border-cream/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-20">
          <div className="md:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.35em] text-[#c99a3d] mb-6">{t.tech.kicker}</div>
            <h2 className="font-serif text-cream font-light text-[42px] md:text-[62px] leading-[1.05] tracking-tight">
              {t.tech.titleA}<br />
              <span className="italic text-cream/70">{t.tech.titleB}</span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <div className="font-mono text-[11px] tracking-[0.3em] text-[#c99a3d] mb-3">{t.tech.stackKicker}</div>
            <p className="text-cream/65 text-[14px] leading-[1.8]">{t.tech.stack}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-24">
          {t.tech.tags.map((tag, i) => {
            const Icon = tagIcons[i];
            return (
              <div key={i} className="card-lift border border-cream/10 rounded-sm p-6 bg-[#0d1526]/50">
                <Icon className="w-5 h-5 text-[#c99a3d] mb-6" strokeWidth={1.4} />
                <div className="font-serif text-cream text-xl mb-2">{tag.title}</div>
                <div className="text-cream/55 text-[12px] leading-[1.65]">{tag.body}</div>
              </div>
            );
          })}
        </div>

        <div className="space-y-28">
          {t.tech.systems.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className={flip ? 'md:order-2' : ''}>
                  <div className="font-mono text-[11px] tracking-[0.35em] text-[#c99a3d] mb-6">{t.tech.system} · {s.n}</div>
                  <h3 className="font-serif text-cream text-[38px] md:text-[52px] leading-[1.05] font-light mb-4">{s.title}</h3>
                  <p className="font-serif italic text-cream/75 text-xl mb-6">{s.lead}</p>
                  <p className="text-cream/65 text-[15px] leading-[1.85] max-w-[560px]">{s.body}</p>

                  <div className="mt-10 space-y-4">
                    {s.specs.map((sp, k) => (
                      <div key={k} className="flex justify-between items-end border-b border-cream/10 pb-3">
                        <span className="font-mono text-[11px] tracking-[0.25em] text-cream/55">{sp[0].toUpperCase()}</span>
                        <span className="font-serif text-cream text-lg">{sp[1]}</span>
                      </div>
                    ))}
                  </div>

                  <a href="#contact" className="group inline-flex items-center gap-2 mt-10 font-mono text-[11px] tracking-[0.28em] text-[#c99a3d] hover:text-cream transition-colors">
                    {t.tech.learn} <ArrowRight className={`w-3.5 h-3.5 transition-transform ${rtl ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} strokeWidth={1.6} />
                  </a>
                </div>

                <div className={`relative aspect-[4/5] max-h-[640px] w-full ${flip ? 'md:order-1' : ''}`}>
                  <div className="absolute inset-0 corner-brackets">
                    <img src={IMAGES.systems[i]} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220]/60 via-transparent to-transparent"></div>
                    <div className={`absolute bottom-6 ${rtl ? 'right-6' : 'left-6'} font-mono text-[10px] tracking-[0.3em] text-cream/85`}>
                      <span className="text-[#c99a3d]">•</span> {s.title.toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Technology;
